/**
 * Mode B — merge Share hub rows into left sidebar + Members grantee panels.
 */

import { fetchGroupsHubOperatorBundle, fetchGroupsHubGranteeFleet } from "../../groups-hub-load.js";
import { revokeGroupsHubBulkRows } from "../../groups-hub-bulk-revoke.js";
import { syncGroupShareGrantees, bulkRevokeGroupShareHub } from "../../group-shares-v1.js";

/**
 * @param {string} ownerAdmin
 * @param {string} groupKey
 * @param {(s: string) => string} canonicalGroupKey
 */
export function hubRowMapKey(ownerAdmin, groupKey, canonicalGroupKey) {
  return `${String(ownerAdmin || "").trim()}\t${canonicalGroupKey(String(groupKey || ""))}`;
}

/**
 * @param {Array<object>} slots
 * @param {object} ctx
 * @returns {Array<object>}
 */
export function ensureActiveGroupInSidebarSlots(slots, ctx) {
  const {
    groupKey,
    tenantOwner,
    metaKey,
    displayFromUrl,
    presence,
    canonicalGroupKey,
  } = ctx;
  const gk = canonicalGroupKey(String(groupKey || ""));
  if (!gk) return Array.isArray(slots) ? slots : [];
  const owner = String(tenantOwner || "").trim();
  const list = Array.isArray(slots) ? [...slots] : [];
  const has = list.some((s) => (
    canonicalGroupKey(String(s.groupKey || "")) === gk
    && String(s.tenantOwner || "").trim() === owner
  ));
  if (has) return list;
  const p = presence && typeof presence === "object" ? presence : { total: 0, online: 0, offline: 0 };
  list.unshift({
    metaKey,
    groupKey: gk,
    tenantOwner: owner,
    groupDisplaySample: String(displayFromUrl || gk),
    serverCounts: {
      total: Number(p.total || 0),
      online: Number(p.online || 0),
      offline: Number(p.offline || 0),
    },
  });
  return list;
}

/**
 * @param {Array<object>} slots
 * @param {Array<object>} hubRows
 * @param {(s: string) => string} canonicalGroupKey
 * @param {(gk: string, owner: string) => string} [groupCardMetaKey]
 */
export function mergeHubRowsIntoSidebarSlots(slots, hubRows, canonicalGroupKey, groupCardMetaKey) {
  const byKey = new Map();
  for (const r of Array.isArray(hubRows) ? hubRows : []) {
    byKey.set(hubRowMapKey(r.ownerAdminForApi, r.groupKey, canonicalGroupKey), r);
  }
  const enriched = (Array.isArray(slots) ? slots : []).map((slot) => {
    const tenant = String(slot.tenantOwner || "").trim();
    let hub = byKey.get(hubRowMapKey(tenant, slot.groupKey, canonicalGroupKey));
    if (!hub) {
      for (const r of hubRows || []) {
        if (canonicalGroupKey(r.groupKey) === canonicalGroupKey(slot.groupKey)) {
          hub = r;
          break;
        }
      }
    }
    if (!hub) {
      return {
        ...slot,
        hasGroupShare: false,
        granteeCount: 0,
        granteesRich: [],
        hubOwnerAdmin: tenant,
        hubGroupDisplay: String(slot.groupDisplaySample || slot.groupKey || ""),
      };
    }
    const rich = Array.isArray(hub.granteesRich) ? hub.granteesRich : [];
    return {
      ...slot,
      hasGroupShare: !!hub.hasGroupShare,
      granteeCount: rich.length || (Array.isArray(hub.grantees) ? hub.grantees.length : 0),
      granteesRich: rich,
      hubOwnerAdmin: hub.ownerAdminForApi,
      hubGroupDisplay: hub.groupDisplayForApi,
    };
  });
  const seen = new Set(
    enriched.map((s) => hubRowMapKey(
      s.hubOwnerAdmin || s.tenantOwner,
      s.groupKey,
      canonicalGroupKey,
    )),
  );
  for (const r of Array.isArray(hubRows) ? hubRows : []) {
    const key = hubRowMapKey(r.ownerAdminForApi, r.groupKey, canonicalGroupKey);
    if (seen.has(key)) continue;
    seen.add(key);
    const rich = Array.isArray(r.granteesRich) ? r.granteesRich : [];
    const mk = typeof groupCardMetaKey === "function"
      ? groupCardMetaKey(r.groupKey, r.ownerAdminForApi)
      : `${r.ownerAdminForApi || ""}:${r.groupKey || ""}`;
    enriched.push({
      metaKey: mk,
      groupKey: canonicalGroupKey(String(r.groupKey || "")),
      tenantOwner: String(r.ownerAdminForApi || "").trim(),
      groupDisplaySample: String(r.groupDisplayForApi || r.displayName || r.groupKey || ""),
      serverCounts: {
        total: Number(r.deviceCount || 0),
        online: 0,
        offline: Number(r.deviceCount || 0),
      },
      hasGroupShare: !!r.hasGroupShare,
      granteeCount: rich.length || (Array.isArray(r.grantees) ? r.grantees.length : 0),
      granteesRich: rich,
      hubOwnerAdmin: r.ownerAdminForApi,
      hubGroupDisplay: r.groupDisplayForApi,
    });
  }
  return enriched;
}

/**
 * @param {Array<object>|undefined} granteesRich
 * @param {(s: string) => string} escapeHtml
 * @param {{ ownerName?: string }} [opts]
 */
export function buildGranteesMembersHtml(granteesRich, escapeHtml, opts = {}) {
  const rich = Array.isArray(granteesRich) ? granteesRich : [];
  const ownerName = String(opts.ownerName || "").trim();
  const parts = [];
  if (ownerName) {
    const initial = escapeHtml(ownerName.charAt(0).toUpperCase() || "O");
    parts.push(
      `<div class="grp-member-row"><span class="grp-member-row__avatar">${initial}</span>` +
      `<div><strong>${escapeHtml(ownerName)}</strong><span class="muted"> · Owner</span></div></div>`,
    );
  }
  if (!rich.length && !parts.length) {
    return '<p class="muted grp-empty-hint">No members yet — use <strong>Add Members</strong> to share this group.</p>';
  }
  for (const gr of rich) {
    const user = escapeHtml(String(gr.username || "").trim());
    if (!user) continue;
    const badge = `${gr.can_view ? "V" : "—"}/${gr.can_operate ? "O" : "—"}`;
    const via = escapeHtml(String(gr.via || "group_share").slice(0, 32));
    const initial = escapeHtml(user.charAt(0).toUpperCase() || "?");
    parts.push(
      `<div class="grp-member-row"><span class="grp-member-row__avatar">${initial}</span>` +
      `<div><strong>${user}</strong>` +
      ` <span class="badge partial" title="view/operate">${badge}</span>` +
      ` <span class="muted" style="font-size:11px">via ${via}</span></div></div>`,
    );
  }
  if (!rich.length && parts.length) {
    parts.push('<p class="muted grp-empty-hint" style="margin:8px 0 0">Not shared yet — no members.</p>');
  }
  return parts.join("");
}

/**
 * @param {ParentNode} view
 * @param {string} membersHtml
 * @param {number} granteeCount
 */
export function paintModeBMembersPanels(view, membersHtml, granteeCount) {
  if (!view) return;
  const full = view.querySelector("#grp-members-full");
  const side = view.querySelector("#grp-members-side");
  if (full) full.innerHTML = membersHtml;
  if (side) side.innerHTML = membersHtml;
  const tab = view.querySelector('[data-grp-tab="members"]');
  if (tab) {
    const n = Number.isFinite(granteeCount) ? granteeCount : 0;
    tab.textContent = n > 0 ? `Members (${n})` : "Members";
  }
  const sideTitle = view.querySelector(".grp-mode-b__right .grp-members-panel .grp-mode-b__section-title");
  if (sideTitle) {
    const n = Number.isFinite(granteeCount) ? granteeCount : 0;
    sideTitle.textContent = n > 0 ? `Members (${n})` : "Members";
  }
}

/**
 * @param {object} slot
 * @param {string} activeGroupKey
 * @param {string} activeTenantOwner
 * @param {(s: string) => string} canonicalGroupKey
 */
export function slotMatchesActiveGroup(slot, activeGroupKey, activeTenantOwner, canonicalGroupKey) {
  const gk = canonicalGroupKey(activeGroupKey || "");
  const owner = String(activeTenantOwner || "").trim();
  return canonicalGroupKey(slot.groupKey || "") === gk
    && String(slot.tenantOwner || "").trim() === owner;
}

/**
 * @param {Array<object>} slots
 * @param {string} activeGroupKey
 * @param {string} activeTenantOwner
 * @param {(s: string) => string} canonicalGroupKey
 */
export function granteesForActiveSlot(slots, activeGroupKey, activeTenantOwner, canonicalGroupKey) {
  const slot = (slots || []).find((s) => slotMatchesActiveGroup(s, activeGroupKey, activeTenantOwner, canonicalGroupKey));
  return slot || null;
}

/**
 * @param {object} ctx
 * @returns {Promise<{ rows: object[], readOnlyHub: boolean, granteeMode: boolean, eligible: boolean }>}
 */
export async function loadModeBHubOperatorRows(ctx) {
  const {
    m,
    api,
    ownerQ = "",
    can,
    principalSeesCustodyDashboard,
    probeGroupsHubAdminHasOwnedDevices: probeOwned,
    buildGroupsHubGranteeRows,
    buildGroupsHubOperatorRows,
    buildGroupsHubOperatorRowsFromSummary,
    groupMetaStorageKey,
    filterNorm = "all",
  } = ctx;

  if (!m) return { rows: [], readOnlyHub: true, granteeMode: false, eligible: false };

  let granteeMode = m.role === "user";
  if (!granteeMode && m.role === "admin") {
    try {
      const hasOwn = await probeOwned(api, { timeoutMs: 22000 });
      if (!hasOwn) granteeMode = true;
    } catch {
      return { rows: [], readOnlyHub: true, granteeMode: true, eligible: false };
    }
  }

  if (granteeMode) {
    try {
      const fleet = await fetchGroupsHubGranteeFleet(api, { timeoutMs: 22000 });
      const devices = Array.isArray(fleet.items) ? fleet.items : [];
      let meta = {};
      try {
        const raw = localStorage.getItem(groupMetaStorageKey());
        meta = raw ? JSON.parse(raw) : {};
      } catch { /* ignore */ }
      const gRows = buildGroupsHubGranteeRows(devices, meta);
      const rows = gRows.map((r) => ({
        groupKey: r.groupKey,
        ownerAdminForApi: r.sharedByOwner,
        groupDisplayForApi: r.groupUrlRaw || r.groupKey,
        hasGroupShare: true,
        granteesRich: [],
        grantees: [],
        displayName: r.displayName,
        deviceCount: r.deviceCount,
      }));
      return { rows, readOnlyHub: true, granteeMode: true, eligible: rows.length > 0 };
    } catch {
      return { rows: [], readOnlyHub: true, granteeMode: true, eligible: false };
    }
  }

  const saReadonly = m.role === "sa_staff" && principalSeesCustodyDashboard(m);
  const adminReadonlyHub = m.role === "admin" && !can("can_manage_users");
  const baseEligible =
    m.role === "superadmin" || (m.role === "admin" && can("can_manage_users")) || adminReadonlyHub || saReadonly;
  const readOnlyHub = saReadonly || adminReadonlyHub;
  if (!baseEligible) {
    return { rows: [], readOnlyHub: true, granteeMode: false, eligible: false };
  }

  if (m.role === "admin") {
    try {
      const hasOwn = await probeOwned(api, { timeoutMs: 22000 });
      if (!hasOwn) return { rows: [], readOnlyHub: true, granteeMode: false, eligible: false };
    } catch {
      return { rows: [], readOnlyHub: true, granteeMode: false, eligible: false };
    }
  }

  let bundle;
  try {
    bundle = await fetchGroupsHubOperatorBundle(api, {
      me: m,
      saReadonly,
      ownerQ,
      seesCustody: principalSeesCustodyDashboard(m),
    }, { timeoutMs: 22000 });
  } catch {
    return { rows: [], readOnlyHub, granteeMode: false, eligible: false };
  }

  let meta = {};
  try {
    const raw = localStorage.getItem(groupMetaStorageKey());
    meta = raw ? JSON.parse(raw) : {};
  } catch { /* ignore */ }

  const { summaryOk, summaryItems, settingsItems, devices, ngItems } = bundle;
  const rows = summaryOk
    ? buildGroupsHubOperatorRowsFromSummary(summaryItems, meta, settingsItems, filterNorm)
    : buildGroupsHubOperatorRows(devices, meta, ngItems, filterNorm, ownerQ, settingsItems);

  return { rows, readOnlyHub, granteeMode: false, eligible: true };
}

/**
 * @param {ParentNode} root
 * @param {object} ctx
 * @returns {() => void}
 */
export function wireGroupSidebarShareActions(root, ctx) {
  const { toast, readOnlyHub } = ctx;
  if (!root || readOnlyHub) return () => {};

  const bar = root.querySelector("#grpSidebarShareActions");
  const bulkBtn = root.querySelector("#grpSidebarBulkRevoke");
  if (bar) bar.hidden = false;

  const syncBulkBtn = () => {
    const n = root.querySelectorAll(".js-grp-sidebar-revoke-sel:checked:not(:disabled)").length;
    if (bulkBtn) bulkBtn.disabled = n === 0;
  };

  const onChange = (ev) => {
    if (ev.target && ev.target.classList && ev.target.classList.contains("js-grp-sidebar-revoke-sel")) {
      syncBulkBtn();
    }
  };

  const onBulkClick = async () => {
    const checked = [...root.querySelectorAll(".js-grp-sidebar-revoke-sel:checked:not(:disabled)")];
    if (!checked.length) return;
    if (
      !confirm(
        `Revoke share for ${checked.length} group(s)? People lose access to those groups and their devices.`,
      )
    ) return;
    const rows = checked.map((cb) => ({
      owner_admin: String(cb.getAttribute("data-owner") || "").trim(),
      group_display: String(cb.getAttribute("data-group") || "").trim(),
    }));
    const result = await revokeGroupsHubBulkRows({
      rows,
      syncGroupShareGrantees,
      bulkRevokeGroupShareHub,
      toast,
    });
    if (result.stoppedBusy) {
      toast(`Stopped early (database busy): ${result.ok} revoked, ${result.fail} skipped/failed.`, "warn");
    } else {
      toast(`Done: ${result.ok} group(s) revoked, ${result.fail} error(s).`, result.fail ? "warn" : "ok");
    }
    if (typeof ctx.onRevokeComplete === "function") await ctx.onRevokeComplete();
    syncBulkBtn();
  };

  root.addEventListener("change", onChange);
  bulkBtn?.addEventListener("click", onBulkClick);
  syncBulkBtn();

  return () => {
    root.removeEventListener("change", onChange);
    bulkBtn?.removeEventListener("click", onBulkClick);
  };
}
